import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import { useTheme } from "../ThemeContext";
import { apifetch } from "../services/interceptors";

// "View all notifications" in the header dropdown has always set activeMenu to
// 'Notifications', but no screen was ever mapped to that key, so the click quietly
// landed on the organization list. This is that screen.
//
// It reuses the endpoints the dropdown already calls; there is no new API here. Worth
// knowing: nothing in the product currently writes to the notifications table, so the
// empty state below is what most users will see. It is built properly for that reason
// rather than treated as an edge case.
const NotificationsScreen = () => {
  const { isDarkMode } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const load = useCallback(async () => {
    try {
      const res = await apifetch("s1/api/notifications?limit=50", { method: "GET", headers: authHeaders() });
      if (!res.ok) throw new Error(`notifications: ${res.status}`);
      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : data.notifications || []);
    } catch (e) {
      console.error("[notifications] load failed", e);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllAsRead = async () => {
    // Optimistic: the list is already on screen and re-fetching to confirm a read flag
    // would make the whole page flash for a change the user just asked for.
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    try {
      await apifetch("s1/api/notifications/read-all", { method: "POST", headers: authHeaders() });
    } catch (e) {
      console.error("[notifications] mark-all failed", e);
      load();
    }
  };

  const hasUnread = notifications.some((n) => !n.is_read);

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center mb-3">
        <h4 className="mb-0">Notifications</h4>
        {hasUnread && (
          <Button variant="link" size="sm" className="ms-auto" onClick={markAllAsRead}>
            Mark all as read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="d-flex align-items-center gap-2 py-4">
          <Spinner animation="border" size="sm" />
          <span className={isDarkMode ? "text-light" : "text-muted"}>Loading notifications…</span>
        </div>
      ) : notifications.length === 0 ? (
        <Card className={isDarkMode ? "bg-dark text-light" : ""}>
          <Card.Body className="text-center py-5">
            <div className="fw-semibold mb-1">Nothing yet</div>
            <div className={isDarkMode ? "text-gray" : "text-muted"}>You are all caught up.</div>
          </Card.Body>
        </Card>
      ) : (
        <Card className={isDarkMode ? "bg-dark text-light" : ""}>
          <ul className="list-unstyled mb-0">
            {notifications.map((n) => (
              <li
                key={n.notification_id}
                className="d-flex align-items-start gap-2 px-3 py-2 border-bottom"
                style={{ opacity: n.is_read ? 0.65 : 1 }}
              >
                <span
                  aria-hidden="true"
                  className="rounded-circle flex-shrink-0 mt-2"
                  style={{ width: 6, height: 6, background: n.is_read ? "transparent" : "#1f7fe5" }}
                />
                <div className="flex-grow-1">
                  <div>{n.message}</div>
                  {n.created_at && (
                    <small className={isDarkMode ? "text-gray" : "text-muted"}>
                      {new Date(n.created_at).toLocaleString()}
                    </small>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </Container>
  );
};

export default NotificationsScreen;
